// apps/admin/src/api/support-tickets.api.ts
//
// Phase 8.2 Wave 8 Task 53 — Support Tickets admin API client.
// Mirrors admin-users.api.ts envelope-unwrap pattern + finance.api.ts
// list-with-meta convention. The corresponding mock backend lives in
// apps/api/src/modules/support (task-52 — canonical contract).
//
// vg-binding: PLAN:task-53
// vg-binding: P8.D-103   — ticket list column set + sort
// vg-binding: P8.D-105   — SLA badge w/ countdown (green/yellow/red)
// vg-binding: P8.D-108   — category filter (Order/Billing/Product/Account)
// vg-binding: P8.D-197   — 6 status states (Open / In Progress / Resolved
//                            / Closed / Reopened + implicit "All" tab)
// vg-binding: P8.D-199   — SLA dual per priority (firstResponseSla +
//                            resolutionSla)
// vg-binding: P8.D-200   — canonical schema (D-223 deprecated per D-248)
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority
//
// Transport contract:
//   • A local `rawAxios` clone keeps the `{ ok, data, meta }` envelope
//     intact so the LIST helper can expose `meta` (page/limit/total)
//     to the TanStack hook + pagination footer. Mutations land via
//     `apiClient` (task-54 detail page), not here.
//   • `unwrapSupportEnvelope` follows the same defensive shape as
//     `unwrapAdminUserEnvelope` — toasts consume `userMessage` directly.

import axios from 'axios';
import { z } from 'zod';
import type { ComposeAttachment } from '@printway/ui';

import { API_BASE_URL } from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// Canonical schema (deprecated).
// ---------------------------------------------------------------------------

/**
 * Ticket priority — 4 levels (each level binds to its own
 * first-response + resolution SLA window).
 */
export const TicketPrioritySchema = z.enum([
  'Critical',
  'High',
  'Medium',
  'Low',
]);
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;

/**
 * Ticket category — 4-set. Filter chip on the list page
 * exposes these literal strings; case-sensitive on the wire.
 */
export const TicketCategorySchema = z.enum([
  'Order',
  'Billing',
  'Product',
  'Account',
  // P9.4.D-17: default category for social/bridge tickets that arrive without a
  // triage category (Messenger/IG/FB-personal). BE canon (ticket.model.ts
  // TICKET_CATEGORIES) has always included it — the FE enum was stale, so every
  // social ticket (category 'General') failed the row schema and was silently
  // dropped from the admin list (and the detail payload was rejected wholesale).
  'General',
]);
export type TicketCategory = z.infer<typeof TicketCategorySchema>;

/**
 * Ticket status — 5 states. The list view stacks an
 * implicit "All" tab on top so the user always has a way back to the
 * unfiltered view. Escalation level is a separate field on the row
 * (not a status), also.
 */
// BE canonical (ticket.model.ts TICKET_STATUSES): no-space variant.
// FE legacy comments referenced `'In Progress'` with a space — accept
// both shapes via union so historical wire payloads still parse.
export const TicketStatusSchema = z.enum([
  'Open',
  'InProgress',
  'In Progress',
  'AwaitingHandler',
  'PendingSupportReview',
  'Resolved',
  'Closed',
  'Reopened',
]);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

/**
 * FE mirror của TICKET_TRANSITIONS (apps/api ticket.handler.ts — Plan 1).
 * Dropdown chuyển trạng thái CHỈ hiện các đích hợp lệ (Closed terminal = rỗng).
 */
export const TICKET_TRANSITIONS_FE: Record<string, TicketStatus[]> = {
  Open: ['InProgress'],
  InProgress: ['Resolved', 'AwaitingHandler'],
  'In Progress': ['Resolved', 'AwaitingHandler'],
  AwaitingHandler: ['PendingSupportReview', 'InProgress'],
  PendingSupportReview: ['InProgress', 'Resolved'],
  Resolved: ['Reopened', 'Closed'],
  Reopened: ['InProgress'],
  Closed: [],
};

export function allowedNextStatuses(current: TicketStatus): TicketStatus[] {
  return TICKET_TRANSITIONS_FE[current] ?? [];
}

/**
 * One row in the ticket list. Mirrors the BE schema declared in task-52
 * with conservative defaults so missing-field drift surfaces as a
 * validation error at the boundary rather than a render crash.
 */
/** Resolution type — 8 values (V2 parity). */
export const TicketResolutionTypeSchema = z.enum([
  'Reprint',
  'FullRefund',
  'PartialRefund',
  'StoreCredit',
  'Replacement',
  'ShippingCredit',
  'NoActionRequired',
  'Escalated',
]);
export type TicketResolutionType = z.infer<typeof TicketResolutionTypeSchema>;

/** Vendor work state once a ticket has been pushed to a vendor. */
export const TicketVendorStatusSchema = z.enum([
  'Pending',
  'InProgress',
  'Done',
]);
export type TicketVendorStatus = z.infer<typeof TicketVendorStatusSchema>;

export const SupportTicketRowSchema = z.object({
  _id: z.string(),
  /** Human-facing code TK<yyyymmdd><seq> — null on legacy rows. */
  ticketNumber: z.string().nullish(),
  /** Derived on the BE: Merchant-opened vs staff-opened (Internal). */
  type: z.enum(['Internal', 'Merchant']).nullish(),
  subject: z.string(),
  description: z.string().optional().default(''),
  merchantId: z.string().nullable(),
  /** Ticket origin — merchant/vendor/admin. Null merchantId ⇒ vendor-opened. */
  origin: z.enum(['merchant', 'vendor', 'admin']).nullish(),
  /** Enriched by BE $lookup (merchants → users) — defensive optional. */
  merchant: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string().optional().default(''),
    })
    .nullish(),
  priority: TicketPrioritySchema,
  category: TicketCategorySchema,
  status: TicketStatusSchema,
  assigneeId: z.string().nullable().optional(),
  assignee: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string().optional().default(''),
    })
    .nullish(),
  /** 0 = no escalation; 1/2/3 = tier-1/2/3 escalated. */
  escalationLevel: z.number().int().min(0).max(3).default(0),
  /** Deadline for first response (priority-bound). BE returns
   *  Unix ms (Number per ticket.model.ts:188); accept ISO string too for
   *  future migration to Date-typed fields. */
  firstResponseSla: z.union([z.string(), z.number()]),
  /** Deadline for resolution (priority-bound). Same shape. */
  resolutionSla: z.union([z.string(), z.number()]),
  firstRespondedAt: z.union([z.string(), z.number()]).nullable().optional(),
  resolvedAt: z.union([z.string(), z.number()]).nullable().optional(),
  createdAt: z.union([z.string(), z.number()]),
  updatedAt: z.union([z.string(), z.number()]).optional(),
  // V2-parity extensions (dbg-support-port) — all defensive-optional.
  resolutionType: TicketResolutionTypeSchema.nullish(),
  resolutionNotes: z.string().nullish(),
  resolvedByUserId: z.string().nullish(),
  rating: z.number().int().min(1).max(5).nullish(),
  ratingComment: z.string().nullish(),
  ratedAt: z.union([z.string(), z.number()]).nullish(),
  vendorId: z.string().nullish(),
  vendor: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string().optional().default(''),
    })
    .nullish(),
  vendorPushedAt: z.union([z.string(), z.number()]).nullish(),
  vendorStatus: TicketVendorStatusSchema.nullish(),
  lastMessagePreview: z.string().nullish(),
  lastMessageAt: z.union([z.string(), z.number()]).nullish(),
  /** Social/bridge channel platform (facebook_personal/telegram/zalo/…) for the
   *  channel badge + filter; null for non-social tickets. */
  channel_type: z.string().nullish(),
  /** Per-caller unread flag (dbg-20260705-ticket-realtime-notify). */
  unread: z.boolean().optional().default(false),
});
export type SupportTicketRow = z.infer<typeof SupportTicketRowSchema>;

/**
 * Pagination + summary block. Per-status counts power the tab pills
 * without a second roundtrip.
 */
export const SupportTicketListMetaSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0).optional(),
  /** Optional per-status counts; missing → fall back to total only. */
  counts: z
    .object({
      all: z.number().int().min(0).optional(),
      open: z.number().int().min(0).optional(),
      in_progress: z.number().int().min(0).optional(),
      resolved: z.number().int().min(0).optional(),
      closed: z.number().int().min(0).optional(),
      reopened: z.number().int().min(0).optional(),
      overdue: z.number().int().min(0).optional(),
    })
    .partial()
    .optional(),
});
export type SupportTicketListMeta = z.infer<typeof SupportTicketListMetaSchema>;

export interface SupportTicketListPayload {
  rows: SupportTicketRow[];
  meta: SupportTicketListMeta;
}

/**
 * Filter shape consumed by the list page. `search` is debounced 300 ms
 * client-side so the cache slice fingerprint only flips on a stable
 * string. `overdue` is a boolean toggle (red chip) that the BE
 * interprets as "now > resolutionSla AND status != Closed".
 */
export interface SupportTicketListFilters {
  /** Status tab (omit for "All"). */
  status?: TicketStatus;
  /** Priority filter chip — single-select dropdown. */
  priority?: TicketPriority;
  /** Category filter chip — single-select dropdown. */
  category?: TicketCategory;
  /** Channel platform filter (facebook_personal/telegram/zalo/…) — server-side,
   *  matches ticket.platformRefs.platform. */
  platform?: string;
  /** Ticket type — Internal (staff-opened) vs Merchant. FE-side filter. */
  type?: 'Internal' | 'Merchant';
  /** Assignee user-id; "unassigned" = open ones with assigneeId=null. */
  assignee?: string;
  /** Overdue-only toggle — red chip row highlight. */
  overdue?: boolean;
  /** Search by ID + subject; debounced 300 ms FE-side. */
  search?: string;
  /** Sort key — defaults BE-side to `createdAt:desc`. */
  sortBy?: 'createdAt' | 'lastMessageAt' | 'priority' | 'resolutionSla';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Error envelope — same defensive shape as admin-users.api.ts.
// ---------------------------------------------------------------------------

export interface SupportTicketApiError {
  ok: false;
  code: string;
  /** Human copy ready for toast — never AxiosError.message. */
  userMessage: string;
  fieldErrors?: Record<string, string[]>;
  requestId?: string;
}

const NETWORK_FALLBACK = 'Network did not respond — please try again.';

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Defensive envelope unwrap.
 *
 * Message priority (per INTERFACE-STANDARDS §message-priority):
 *   1. error.user_message
 *   2. error.message
 *   3. message (legacy top-level)
 *   4. NETWORK_FALLBACK
 */
export function unwrapSupportEnvelope<TData, TMeta = undefined>(
  payload: unknown,
):
  | { ok: true; data: TData; meta: TMeta; requestId?: string }
  | SupportTicketApiError {
  if (payload == null || typeof payload !== 'object') {
    return { ok: false, code: 'NETWORK_ERROR', userMessage: NETWORK_FALLBACK };
  }
  const body = payload as Record<string, unknown>;

  if ((body.ok === true || body.success === true) && 'data' in body) {
    return {
      ok: true,
      data: body.data as TData,
      meta: (body.meta ?? undefined) as TMeta,
      requestId:
        pickString(body.request_id) ?? pickString(body.requestId),
    };
  }

  const errRaw = body.error;
  if (
    (body.ok === false || body.success === false) &&
    errRaw &&
    typeof errRaw === 'object'
  ) {
    const err = errRaw as Record<string, unknown>;
    const out: SupportTicketApiError = {
      ok: false,
      code: pickString(err.code) ?? 'UNKNOWN_ERROR',
      userMessage:
        pickString(err.user_message) ??
        pickString(err.message) ??
        pickString(body.message) ??
        NETWORK_FALLBACK,
    };
    if (err.field_errors && typeof err.field_errors === 'object') {
      out.fieldErrors = err.field_errors as Record<string, string[]>;
    }
    const reqId = pickString(err.request_id) ?? pickString(body.request_id);
    if (reqId) out.requestId = reqId;
    return out;
  }

  return { ok: false, code: 'UNKNOWN_SHAPE', userMessage: NETWORK_FALLBACK };
}

// ---------------------------------------------------------------------------
// Local axios clone — keeps both `data` (rows) AND `meta` (page +
// per-status counts) on the wire. CSRF header is unnecessary on GETs.
// ---------------------------------------------------------------------------

const rawAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

// CSRF double-submit interceptor — shared apiClient handles this on the main
// instance, but this local clone bypasses that pipeline. Mutating verbs
// (POST/PUT/PATCH/DELETE) read the XSRF-TOKEN cookie (NOT HttpOnly) and echo
// it back as X-XSRF-TOKEN; the BE auth plugin enforces the match. Without
// this header the escalate / patch / message-post calls return 403
// AUTH_CSRF_MISSING.
function readSupportCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
  return m && typeof m[1] === 'string' ? decodeURIComponent(m[1]) : null;
}
rawAxios.interceptors.request.use((cfg) => {
  const method = (cfg.method ?? 'get').toUpperCase();
  if (
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE'
  ) {
    const csrf = readSupportCsrfToken();
    if (csrf) {
      cfg.headers = cfg.headers ?? {};
      // Both Axios v0.x (plain object) + v1.x (AxiosHeaders) paths.
      if (
        typeof (cfg.headers as { set?: (k: string, v: string) => void }).set ===
        'function'
      ) {
        (cfg.headers as { set: (k: string, v: string) => void }).set(
          'X-XSRF-TOKEN',
          csrf,
        );
      } else {
        (cfg.headers as Record<string, string>)['X-XSRF-TOKEN'] = csrf;
      }
    }
  }
  return cfg;
});

function buildQs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.length === 0) continue;
    if (typeof v === 'boolean') {
      if (v) sp.set(k, 'true');
      continue;
    }
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// LIST helper — GET /api/v1/tickets
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/tickets — returns the admin-facing ticket list with the
 * canonical envelope shape. Filters serialise via `buildQs`;
 * `overdue=true` is the toggle that the BE expands to `now >
 * resolutionSla AND status != Closed`.
 */
export async function listSupportTickets(
  filters: SupportTicketListFilters = {},
): Promise<SupportTicketListPayload> {
  // Param names track the BE `listQuerySchema` in
  // apps/api/src/modules/support/ticket.routes.ts:
  //   q          (NOT search)
  //   assignee_id (NOT assignee)
  //   pageSize    (NOT limit)
  // Mismatched names silently no-op on the BE because additionalProperties:true
  // accepts them without effect.
  const params: Record<string, unknown> = {
    page: filters.page ?? 1,
    pageSize: filters.limit ?? 20,
  };
  if (filters.status) params.status = filters.status;
  if (filters.priority) params.priority = filters.priority;
  if (filters.category) params.category = filters.category;
  if (filters.platform) params.platform = filters.platform;
  if (filters.assignee) params.assignee_id = filters.assignee;
  if (filters.overdue) params.overdue = true;
  if (filters.search) params.q = filters.search;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;

  const path = `/api/v1/tickets${buildQs(params)}`;

  let body: unknown;
  try {
    const res = await rawAxios.get(path);
    body = res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown, unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies SupportTicketApiError;
  }

  const unwrap = unwrapSupportEnvelope<unknown, unknown>(body);
  if (!unwrap.ok) throw unwrap;

  // BE returns `data: { rows, page, pageSize, total }`, NOT a top-level
  // `meta` block. Earlier code parsed `unwrap.data` as an array and
  // `unwrap.meta` as meta — both failed schema → INVALID_PAYLOAD throw →
  // TanStack retry:1 → silent double-fire on every mount.
  // Fix: extract `rows` from data + synthesize meta from {page, pageSize→limit, total}.
  const dataObj =
    unwrap.data && typeof unwrap.data === 'object'
      ? (unwrap.data as Record<string, unknown>)
      : {};
  const rowsRaw = Array.isArray(dataObj.rows) ? dataObj.rows : [];
  const metaSynth: Record<string, unknown> = {
    page: Number(dataObj.page ?? 1),
    limit: Number(dataObj.pageSize ?? dataObj.limit ?? 20),
    total: Number(dataObj.total ?? 0),
  };
  if (dataObj.totalPages !== undefined) metaSynth.totalPages = Number(dataObj.totalPages);
  if (dataObj.counts !== undefined) metaSynth.counts = dataObj.counts;

  // Resilient per-row parse (dbg-20260708): a single legacy/odd row must not
  // blank the whole list. Previously `z.array(...).safeParse` was all-or-
  // nothing → one bad row → INVALID_PAYLOAD → "0 of 0 tickets" even though the
  // BE returned a full page. Drop invalid rows (warn), keep the valid ones.
  const rows: SupportTicketRow[] = [];
  for (const raw of rowsRaw) {
    const p = SupportTicketRowSchema.safeParse(raw);
    if (p.success) {
      rows.push(p.data);
    } else {
      if (typeof console !== 'undefined') {
        const code = (raw as { ticketNumber?: string; _id?: string })
          ?.ticketNumber ??
          (raw as { _id?: string })?._id ??
          '?';
        console.warn(
          `[support] ticket row ${code} failed schema, skipped:`,
          p.error.issues,
        );
      }
    }
  }
  const metaParse = SupportTicketListMetaSchema.safeParse(metaSynth);
  if (!metaParse.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Response data did not match the expected format.',
    } satisfies SupportTicketApiError;
  }

  return { rows, meta: metaParse.data };
}

/**
 * Stats payload for the support dashboard (task-55). Re-exported here
 * so the list page can preview overall counts above the table when the
 * BE doesn't ship per-status `counts` inside the list `meta` block.
 */
export interface SupportTicketStats {
  open: number;
  /** Active tickets at top priority (Critical) — V2 "Urgent" stat card. */
  urgent: number;
  /** Tickets unread by the current caller — sidebar Support badge feed. */
  unread: number;
  in_progress: number;
  resolved: number;
  closed: number;
  reopened: number;
  overdue: number;
  /** Resolved since local midnight — V2 "Resolved Today" stat card. */
  resolved_today: number;
  /** Total non-deleted tickets — list-page "All tickets" stat card. */
  total: number;
  /** Per-status counts — list-page stat cards (dbg-20260706-184637). */
  by_status: Record<TicketStatus, number>;
  /** Optional aggregate buckets the dashboard page (task-55) consumes. */
  byPriority?: Partial<Record<TicketPriority, number>>;
  byCategory?: Partial<Record<TicketCategory, number>>;
}

/**
 * GET /api/v1/tickets/stats — lightweight summary endpoint.
 * Used by the dashboard card on task-55; the list page falls back to
 * `meta.counts` when present and only calls this when explicitly
 * requested.
 */
export async function getSupportTicketStats(): Promise<SupportTicketStats> {
  let body: unknown;
  try {
    const res = await rawAxios.get('/api/v1/tickets/stats');
    body = res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies SupportTicketApiError;
  }

  const unwrap = unwrapSupportEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;

  const data = (unwrap.data ?? {}) as Record<string, unknown>;
  const rawByStatus = (data.by_status ?? {}) as Record<string, unknown>;
  const byStatus: Record<TicketStatus, number> = {
    Open: Number(rawByStatus.Open ?? 0),
    InProgress: Number(rawByStatus.InProgress ?? 0),
    // Admin TicketStatus carries a spaced 'In Progress' alias; the BE only
    // emits 'InProgress', so mirror it here to satisfy the exhaustive Record.
    'In Progress': Number(rawByStatus.InProgress ?? 0),
    AwaitingHandler: Number(rawByStatus.AwaitingHandler ?? 0),
    PendingSupportReview: Number(rawByStatus.PendingSupportReview ?? 0),
    Resolved: Number(rawByStatus.Resolved ?? 0),
    Closed: Number(rawByStatus.Closed ?? 0),
    Reopened: Number(rawByStatus.Reopened ?? 0),
  };
  return {
    open: Number(data.open ?? 0),
    urgent: Number(data.urgent ?? 0),
    unread: Number(data.unread ?? 0),
    in_progress: Number(data.in_progress ?? byStatus.InProgress),
    resolved: Number(data.resolved ?? byStatus.Resolved),
    closed: Number(data.closed ?? byStatus.Closed),
    reopened: Number(data.reopened ?? byStatus.Reopened),
    overdue: Number(data.overdue ?? 0),
    resolved_today: Number(data.resolved_today ?? 0),
    total: Number(
      data.total ??
        byStatus.Open +
          byStatus.InProgress +
          byStatus.Resolved +
          byStatus.Closed +
          byStatus.Reopened,
    ),
    by_status: byStatus,
    byPriority: (data.byPriority ?? undefined) as
      | SupportTicketStats['byPriority']
      | undefined,
    // BE /tickets/stats ships per-category counts under `volume_by_category`
    // (see ticket.handler.ts). Fall back to it so the list-page quick filters
    // get real counts; `byCategory` kept as an accepted alias.
    byCategory: (data.byCategory ?? data.volume_by_category ?? undefined) as
      | SupportTicketStats['byCategory']
      | undefined,
  };
}

// ===========================================================================
// Phase 8.2 Wave 8 Task 54 — Detail page endpoints (detail + patch + message
// + escalate + attachment + admin picker).
//
// vg-binding: PLAN:task-54
// vg-binding: P8.D-104  — detail page 65/35 layout (this client wires the
//                          GET/PATCH + POST /messages the layout consumes)
// vg-binding: P8.D-109  — escalation modal endpoint (pick / round-robin +
//                          note + 4h SLA default)
// vg-binding: P8.D-133  — escalation writes audit (BE-side, FE-blind)
// vg-binding: P8.D-197  — escalation does NOT change status; bumps
//                          escalationLevel separately
// vg-binding: P8.D-216  — attachments on messages (multipart upload)
// ===========================================================================

/** Idempotency-Key helper — shared with the detail-page mutations. */
export function newIdempotencyKey(): string {
  const c =
    typeof crypto !== 'undefined'
      ? (crypto as Crypto & { randomUUID?: () => string })
      : null;
  if (c?.randomUUID) return c.randomUUID();
  return (
    'idem_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

/** Message thread marker — public (merchant-visible), internal (staff
 *  note), vendor (separate admin↔vendor thread; merchant-invisible). */
export const TicketMessageTypeSchema = z.enum(['public', 'internal', 'vendor']);
export type TicketMessageType = z.infer<typeof TicketMessageTypeSchema>;

/**
 * Attachment row attached to a ticket or one of its messages.
 *
 * Plan 3 Task 8 — the BE now returns typed attachments in two shapes:
 *   • file:         { fileId, filename, contentType, size, url }
 *   • external-url: { kind: 'external-url', url }   (pasted image link)
 * The preprocess maps the spec-named BE fields (fileId/contentType/size) onto
 * the FE-facing aliases (id/mimeType/sizeBytes) and preserves `kind` so the
 * transcript can pick inline-image vs download rendering (isImageAttachment).
 * Legacy `{ id, mimeType, sizeBytes }` payloads still parse (aliases win when
 * the spec names are absent).
 */
export const TicketAttachmentSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const r = raw as Record<string, unknown>;
  // Typed external-url attachment (pasted image link) — no fileId/filename.
  if (r.kind === 'external-url' && typeof r.url === 'string') {
    return {
      kind: 'external-url',
      id: r.url,
      filename: r.url,
      mimeType: 'external-url',
      sizeBytes: 0,
      url: r.url,
    };
  }
  return {
    kind: r.kind ?? 'file',
    id: r.id ?? r.fileId,
    filename: r.filename,
    mimeType: r.mimeType ?? r.contentType,
    sizeBytes: r.sizeBytes ?? r.size,
    url: r.url,
  };
}, z.object({
  kind: z.enum(['file', 'external-url']).optional().default('file'),
  id: z.string(),
  filename: z.string(),
  mimeType: z.string().optional().default('application/octet-stream'),
  sizeBytes: z.number().int().min(0).optional().default(0),
  // BE emits `url: null` for message-embedded attachments (the serve URL is
  // resolved lazily; only the /attachments upload endpoint returns a string).
  // `.optional()` rejects null → POST-message parse failed with "Message
  // creation response had an invalid format" (dbg-20260710). `.nullish()`
  // accepts null|undefined, matching the wire shape.
  url: z.string().nullish(),
}));
export type TicketAttachment = z.infer<typeof TicketAttachmentSchema>;

/** Single chat message in the conversation thread.
 *
 *  BE serializes via toMessagePayload (apps/api/src/modules/support/ticket.handler.ts):
 *    { _id, ticketId, authorId, type, content, attachments: string[], createdAt }
 *
 *  FE schema below uses { id, type, authorId, authorName, body, attachments: object[] }.
 *  The preprocess step normalises BE → FE so a single ticket message anywhere
 *  in the detail payload doesn't trip the parse and surface "Dữ liệu chi tiết
 *  ticket sai định dạng" on the detail page.
 */
const _ticketMessagePreprocess = (raw: unknown): unknown => {
  if (!raw || typeof raw !== 'object') return raw;
  const r = raw as Record<string, unknown>;
  const id = r.id ?? r._id;
  const body = r.body ?? r.content ?? '';
  const authorName =
    r.authorName ??
    r.author_name ??
    (typeof r.authorId === 'string' && r.authorId.length > 0
      ? `Admin ${String(r.authorId).slice(-6)}`
      : 'System');
  const att = Array.isArray(r.attachments) ? r.attachments : [];
  const attachments = att.map((a, i) => {
    if (a && typeof a === 'object' && !Array.isArray(a)) {
      // Plan 3 Task 9b — pass typed attachments through UNCHANGED so
      // TicketAttachmentSchema's preprocess can map fileId/contentType/size +
      // preserve `kind` (needed for inline-image detection). Legacy object
      // shapes without fileId/id still get an id/filename fallback here.
      const ao = a as Record<string, unknown>;
      return {
        ...ao,
        id: ao.id ?? ao.fileId ?? ao._id ?? `att-${i}`,
        filename: ao.filename ?? ao.name ?? ao.url ?? 'attachment',
      };
    }
    // Bare string id (BE legacy shape) → minimal {id, filename} chip.
    const s = typeof a === 'string' ? a : `att-${i}`;
    return { id: s, filename: s };
  });
  return {
    ...r,
    id: id != null ? String(id) : undefined,
    body: String(body),
    authorName: String(authorName),
    attachments,
  };
};

export const TicketMessageSchema = z.preprocess(
  _ticketMessagePreprocess,
  z.object({
    id: z.string(),
    type: TicketMessageTypeSchema,
    // Inbound social/bridge messages carry no internal author → null.
    authorId: z.string().nullable(),
    authorName: z.string(),
    body: z.string(),
    attachments: z.array(TicketAttachmentSchema).optional().default([]),
    createdAt: z.union([z.string(), z.number()]).transform((v) =>
      typeof v === 'number' ? new Date(v).toISOString() : v,
    ),
    /** Owning dispatch id when this message belongs to a vendor/department
     *  dispatch thread (dbg-20260709) — null for the merchant conversation. */
    dispatchId: z.string().nullish(),
    /** Social/bridge provenance (null on merchant/staff rows). direction aligns
     *  the bubble (inbound=left/customer, outbound=right/agent); deliveryStatus
     *  drives the send-status badge. */
    direction: z.enum(['inbound', 'outbound']).nullish(),
    deliveryStatus: z.enum(['pending', 'sent', 'failed', 'blocked_window']).nullish(),
    platformErrorCode: z.string().nullish(),
  }),
);
export type TicketMessage = z.infer<typeof TicketMessageSchema>;

/** Audit-flavoured timeline entry shown in the right-side sidebar. */
export const TicketActivityEntrySchema = z.object({
  id: z.string(),
  eventType: z.string(),
  actorId: z.string().nullable().optional(),
  actorName: z.string().nullable().optional(),
  detail: z.string(),
  createdAt: z.string(),
});
export type TicketActivityEntry = z.infer<typeof TicketActivityEntrySchema>;

/** Related-order chip displayed in the sidebar. */
export const TicketRelatedOrderSchema = z.object({
  id: z.string(),
  orderCode: z.string(),
  status: z.string().optional(),
});
export type TicketRelatedOrder = z.infer<typeof TicketRelatedOrderSchema>;

/**
 * Full detail payload returned by GET /api/v1/tickets/:id.
 *
 * Extends the list-row schema with description, requester block, message
 * history, audit-flavoured activity timeline, and related-order chips. All
 * arrays default to `[]` so a freshly-created ticket renders cleanly even
 * before the BE emits its first audit entry.
 */
export const SupportTicketDetailSchema = SupportTicketRowSchema.extend({
  description: z.string().optional().default(''),
  /** Read-receipt cursor: newest lastReadAt among non-caller participants
   *  (BE getCounterpartyReadAt). Staff messages with createdAt ≤ this are
   *  rendered as "read by merchant" (dbg-20260707-034500). */
  counterpartyReadAt: z.union([z.string(), z.number()]).nullish(),
  requesterId: z.string().nullable().optional(),
  requesterName: z.string().nullable().optional(),
  requesterEmail: z.string().nullable().optional(),
  attachments: z.array(TicketAttachmentSchema).optional().default([]),
  messages: z.array(TicketMessageSchema).optional().default([]),
  activity: z.array(TicketActivityEntrySchema).optional().default([]),
  relatedOrders: z.array(TicketRelatedOrderSchema).optional().default([]),
  /** Merchant Information card (V2 parity) — order count + lifetime value. */
  merchantStats: z
    .object({
      orders: z.number().int().min(0),
      lifetime_usd_cents: z.number().int().min(0),
    })
    .nullish(),
  /** Plan D — social/bridge embed (BE getTicketHandler buildSocialEmbed). Was
   *  previously stripped by this schema so the detail page could never branch
   *  on it. `platform` is a loose string to also accept the bridge variants
   *  (facebook_personal/zalo_personal). */
  social: z
    .object({
      platform: z.string(),
      channelId: z.string().optional(),
      replyWindow: z.object({ open: z.boolean(), expiresAt: z.string().nullable() }),
      requesterExternal: z
        .object({ name: z.string(), avatar: z.string().nullable(), platformUserId: z.string() })
        .nullable(),
      linkedAccount: z.object({ kind: z.string(), id: z.string() }).nullable(),
    })
    .nullish(),
});
export type SupportTicketDetail = z.infer<typeof SupportTicketDetailSchema>;

/** Admin option for assignee select + escalation modal "Pick admin" mode. */
export const SupportAdminOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().optional(),
  role: z.string().optional(),
});
export type SupportAdminOption = z.infer<typeof SupportAdminOptionSchema>;

// ---------------------------------------------------------------------------
// GET /api/v1/tickets/:id
// ---------------------------------------------------------------------------

export async function getSupportTicketDetail(
  ticketId: string,
): Promise<SupportTicketDetail> {
  let body: unknown;
  try {
    const r = await rawAxios.get(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}`,
    );
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies SupportTicketApiError;
  }

  const unwrap = unwrapSupportEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;

  // BE wraps detail as `{ticket: {...}, messages: [], activity: [...] }`
  // (ticket.handler.ts). Older clients expected the flat shape with ticket
  // fields at top level. Coalesce both shapes so FE accepts either.
  const dataObj =
    unwrap.data && typeof unwrap.data === 'object'
      ? (unwrap.data as Record<string, unknown>)
      : {};
  const innerTicket =
    dataObj.ticket && typeof dataObj.ticket === 'object'
      ? (dataObj.ticket as Record<string, unknown>)
      : dataObj;
  const parseTarget: Record<string, unknown> = {
    ...innerTicket,
    messages: Array.isArray(dataObj.messages)
      ? dataObj.messages
      : Array.isArray(innerTicket.messages)
        ? innerTicket.messages
        : [],
    activity: Array.isArray(dataObj.activity)
      ? dataObj.activity
      : Array.isArray(innerTicket.activity)
        ? innerTicket.activity
        : [],
    attachments: Array.isArray(dataObj.attachments)
      ? dataObj.attachments
      : Array.isArray(innerTicket.attachments)
        ? innerTicket.attachments
        : [],
    relatedOrders: Array.isArray(dataObj.relatedOrders)
      ? dataObj.relatedOrders
      : Array.isArray(innerTicket.relatedOrders)
        ? innerTicket.relatedOrders
        : [],
    // Wrapper-level block (sits beside `ticket`, not inside it).
    merchantStats: dataObj.merchantStats ?? innerTicket.merchantStats ?? null,
  };

  const parsed = SupportTicketDetailSchema.safeParse(parseTarget);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Ticket detail response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/tickets/:id — status / priority / assignee / category.
// escalation level is NOT updated here; use the escalate endpoint.
// ---------------------------------------------------------------------------

export interface UpdateSupportTicketRequest {
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  /** null = unassign. */
  assigneeId?: string | null;
  /** Captured with status='Resolved' (V2 Resolution card). */
  resolutionType?: TicketResolutionType;
  resolutionNotes?: string;
}

export async function updateSupportTicket(
  ticketId: string,
  body: UpdateSupportTicketRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<SupportTicketDetail> {
  let res: unknown;
  try {
    const r = await rawAxios.patch(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}`,
      body,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies SupportTicketApiError;
    }
  }

  const unwrap = unwrapSupportEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  const parsed = SupportTicketDetailSchema.safeParse(unwrap.data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Ticket update response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Unread cursor (dbg-20260705-ticket-realtime-notify)
// POST /api/v1/tickets/:id/read  · POST /api/v1/tickets/read-all
// ---------------------------------------------------------------------------

/** Mark a single ticket read for the current caller. Best-effort. */
export async function markTicketRead(ticketId: string): Promise<void> {
  try {
    await rawAxios.post(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}/read`,
      {},
    );
  } catch (err) {
    // Unread cursor is non-critical UI state — surface API-shaped errors but
    // never a raw network throw (the row just stays highlighted).
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
  }
}

/** Mark every in-scope ticket read for the caller. Returns count marked. */
export async function markAllTicketsRead(): Promise<number> {
  try {
    const r = await rawAxios.post('/api/v1/tickets/read-all', {});
    const unwrap = unwrapSupportEnvelope<{ count?: number }>(r.data);
    if (unwrap.ok) {
      return Number((unwrap.data as { count?: number })?.count ?? 0);
    }
  } catch {
    // best-effort
  }
  return 0;
}

// ---------------------------------------------------------------------------
// PUT /api/v1/tickets/:id/related-orders — set the ticket's linked orders at
// ticket level (sidebar Related Orders card). Replaces the per-message
// relatedOrderIds staging from the reply composer (dbg-20260708).
// ---------------------------------------------------------------------------

/** Set the full linked-order set for a ticket. Throws SupportTicketApiError. */
export async function setTicketRelatedOrders(
  ticketId: string,
  orderIds: string[],
): Promise<void> {
  try {
    const r = await rawAxios.put(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}/related-orders`,
      { orderIds },
    );
    const unwrap = unwrapSupportEnvelope<unknown>(r.data);
    if (!unwrap.ok) throw unwrap;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      return;
    }
    if ((err as SupportTicketApiError)?.ok === false) throw err;
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies SupportTicketApiError;
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/tickets/:id/messages
// `type=internal` MUST NOT pause the SLA — BE handles; FE just
// flags the badge.
// ---------------------------------------------------------------------------

export interface PostTicketMessageRequest {
  type: TicketMessageType;
  /** Message body. BE schema (ticket.routes.ts:messageBodySchema) requires the
   *  field be named `content`; using `body` returns 400 FST_ERR_VALIDATION. */
  content: string;
  /** Typed attachments (Plan 3 Task 8) — file (GridFS) or external-url (pasted
   *  image link). BE schema field is `attachments` (NOT `attachmentIds`). */
  attachments?: ComposeAttachment[];
  /** unified-ticket §7.5 — orders referenced by this message (roll-up to the
   *  parent ticket BE-side). */
  relatedOrderIds?: string[];
}

export async function postTicketMessage(
  ticketId: string,
  payload: PostTicketMessageRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<TicketMessage> {
  let res: unknown;
  try {
    const r = await rawAxios.post(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}/messages`,
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies SupportTicketApiError;
    }
  }

  const unwrap = unwrapSupportEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  const parsed = TicketMessageSchema.safeParse(unwrap.data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Message creation response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// POST /api/v1/tickets/:id/attachments — multipart upload.
// Returns the persisted attachment (id + url) so the reply box can collect
// IDs and submit them with the next message post.
// ---------------------------------------------------------------------------

export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
): Promise<TicketAttachment> {
  const form = new FormData();
  form.append('file', file, file.name);

  let res: unknown;
  try {
    const r = await rawAxios.post(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies SupportTicketApiError;
    }
  }

  const unwrap = unwrapSupportEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  const parsed = TicketAttachmentSchema.safeParse(unwrap.data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Attachment upload response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// POST /api/v1/tickets/:id/escalate
// admin picker + note + SLA 4h default.
// BE writes the audit entry.
// escalation level bumps separately; status remains untouched.
// ---------------------------------------------------------------------------

export interface EscalateTicketRequest {
  /** Mode: explicit admin pick OR round-robin over the on-duty queue. */
  mode: 'pick' | 'round_robin';
  /** Required when mode === 'pick'. */
  toAdminId?: string;
  /** Free-text reason; persisted on the audit entry. */
  note: string;
  /** SLA window in hours; defaults BE-side to 4h. */
  slaHours?: number;
}

export async function escalateSupportTicket(
  ticketId: string,
  payload: EscalateTicketRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<SupportTicketDetail> {
  let res: unknown;
  try {
    const r = await rawAxios.post(
      `/api/v1/tickets/${encodeURIComponent(ticketId)}/escalate`,
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies SupportTicketApiError;
    }
  }

  const unwrap = unwrapSupportEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  const parsed = SupportTicketDetailSchema.safeParse(unwrap.data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Ticket escalation response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// GET /api/v1/tickets/admins — admin options for assignee + escalate pickers.
// ---------------------------------------------------------------------------

const _AdminOptionsHandlerShape = z.object({
  admins: z.array(SupportAdminOptionSchema),
});

export async function listTicketAdmins(): Promise<SupportAdminOption[]> {
  let body: unknown;
  try {
    const r = await rawAxios.get('/api/v1/tickets/admins');
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies SupportTicketApiError;
  }

  const unwrap = unwrapSupportEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;

  // Tolerate either { admins: [...] } or a bare array.
  const wrapped = _AdminOptionsHandlerShape.safeParse(unwrap.data);
  if (wrapped.success) return wrapped.data.admins;
  const bare = z.array(SupportAdminOptionSchema).safeParse(unwrap.data);
  if (bare.success) return bare.data;
  throw {
    ok: false,
    code: 'INVALID_PAYLOAD',
    userMessage: 'Admin list response had an invalid format.',
  } satisfies SupportTicketApiError;
}

// ===========================================================================
// dbg-support-port — V2-parity endpoints (create / push-vendor / activity /
// performance / inbox / channels / picker options).
// ===========================================================================

async function getUnwrapped(path: string): Promise<unknown> {
  let body: unknown;
  try {
    const r = await rawAxios.get(path);
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies SupportTicketApiError;
  }
  const unwrap = unwrapSupportEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;
  return unwrap.data;
}

async function sendUnwrapped(
  method: 'post' | 'patch' | 'delete',
  path: string,
  payload?: unknown,
): Promise<unknown> {
  let body: unknown;
  try {
    const r =
      method === 'delete'
        ? await rawAxios.delete(path)
        : await rawAxios[method](path, payload, {
            headers: { 'Idempotency-Key': newIdempotencyKey() },
          });
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      body = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies SupportTicketApiError;
    }
  }
  const unwrap = unwrapSupportEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;
  return unwrap.data;
}

// ---- create (admin New Ticket modal) --------------------------------------

export interface CreateSupportTicketRequest {
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  /** Required for admin-created tickets — opened on behalf of a merchant. */
  merchantId: string;
}

export async function createSupportTicket(
  payload: CreateSupportTicketRequest,
): Promise<SupportTicketRow> {
  const data = await sendUnwrapped('post', '/api/v1/tickets', payload);
  const parsed = SupportTicketRowSchema.safeParse(data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Ticket create response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---- per-ticket activity log ------------------------------------------------

/** Per-field diff attached to `support_ticket.updated` entries (G-14). */
export const TicketAuditChangeSchema = z.object({
  field: z.string(),
  from: z.unknown().nullish(),
  to: z.unknown().nullish(),
});
export type TicketAuditChange = z.infer<typeof TicketAuditChangeSchema>;

export const TicketAuditActivitySchema = z.object({
  id: z.string(),
  action: z.string(),
  actorEmail: z.string().optional().default(''),
  actorRole: z.string().optional().default(''),
  createdAt: z.union([z.string(), z.number()]).nullable(),
  /** Optional per-field diff — render when present, tolerate absence. */
  changes: z.array(TicketAuditChangeSchema).optional().default([]),
});
export type TicketAuditActivity = z.infer<typeof TicketAuditActivitySchema>;

export async function getTicketActivity(
  ticketId: string,
): Promise<TicketAuditActivity[]> {
  const data = await getUnwrapped(
    `/api/v1/tickets/${encodeURIComponent(ticketId)}/activity`,
  );
  // Shape tolerance (dbg G-14 — activity rendered empty while the API had
  // rows): entries may arrive as the `data` array itself, or nested under
  // `entries`/`items`. Per-row safeParse keeps one malformed entry from
  // silently dropping the whole log.
  const obj = (data ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(data)
    ? (data as unknown[])
    : Array.isArray(obj.entries)
      ? (obj.entries as unknown[])
      : Array.isArray(obj.items)
        ? (obj.items as unknown[])
        : [];
  const out: TicketAuditActivity[] = [];
  for (const entry of raw) {
    const parsed = TicketAuditActivitySchema.safeParse(entry);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

// ---- performance analytics ---------------------------------------------------

export interface SupportPerformanceFilters {
  from?: number;
  to?: number;
  category?: TicketCategory;
  agentId?: string;
  rating?: number;
}

export const SupportPerformanceSchema = z.object({
  total_rated: z.number(),
  average_rating: z.number().nullable(),
  satisfaction_rate: z.number().nullable(),
  avg_response_seconds: z.number().nullable(),
  rating_distribution: z.record(z.string(), z.number()),
  leaderboard: z.array(
    z.object({
      agent_id: z.string().nullable(),
      name: z.string(),
      tickets_handled: z.number(),
      avg_rating: z.number().nullable(),
      avg_response_seconds: z.number().nullable(),
      sla_breach: z.number(),
    }),
  ),
  category_breakdown: z.array(
    z.object({
      category: z.string(),
      tickets_handled: z.number(),
      avg_rating: z.number().nullable(),
    }),
  ),
});
export type SupportPerformance = z.infer<typeof SupportPerformanceSchema>;

export async function getSupportPerformance(
  filters: SupportPerformanceFilters = {},
): Promise<SupportPerformance> {
  const qs = buildQs({
    from: filters.from,
    to: filters.to,
    category: filters.category,
    agent_id: filters.agentId,
    rating: filters.rating,
  });
  const data = await getUnwrapped(`/api/v1/tickets/performance${qs}`);
  const parsed = SupportPerformanceSchema.safeParse(data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Performance response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

// ---- inbox ------------------------------------------------------------------

export const SupportInboxConversationSchema = z.object({
  ticket_id: z.string(),
  subject: z.string(),
  status: TicketStatusSchema,
  priority: TicketPrioritySchema,
  merchant: z
    .object({ id: z.string(), name: z.string(), email: z.string().optional() })
    .nullish(),
  assignee: z
    .object({ id: z.string(), name: z.string(), email: z.string().optional() })
    .nullish(),
  last_message_preview: z.string().nullish(),
  last_message_at: z.union([z.string(), z.number()]).nullish(),
  /** Plan D — channel platform (from BE platformRefs.platform) so the inbox
   *  can badge bridge/social conversations. Null for non-channel tickets. */
  channel_type: z.string().nullish(),
  /** Per-caller unread flag (admin-inbox port) — same read-cursor mechanism
   *  as the ticket list; drives the bold row + dot in the conversation list. */
  unread: z.boolean().optional().default(false),
});
export type SupportInboxConversation = z.infer<
  typeof SupportInboxConversationSchema
>;

/** Stable per-tab totals for the All / Open / Assigned-to-me filter chips. */
export const SupportInboxCountsSchema = z.object({
  all: z.number().int().min(0).catch(0),
  open: z.number().int().min(0).catch(0),
  assigned_to_me: z.number().int().min(0).catch(0),
});
export type SupportInboxCounts = z.infer<typeof SupportInboxCountsSchema>;

export interface SupportInboxResult {
  conversations: SupportInboxConversation[];
  counts: SupportInboxCounts;
}

export async function getSupportInbox(filters: {
  scope?: 'all' | 'assigned_to_me';
  status?: 'open';
  q?: string;
} = {}): Promise<SupportInboxResult> {
  const data = await getUnwrapped(
    `/api/v1/tickets/inbox${buildQs({ ...filters })}`,
  );
  const obj = (data ?? {}) as Record<string, unknown>;
  // Per-row parse (NOT z.array().safeParse) — one malformed conversation must
  // not blank the ENTIRE inbox. Mirrors the ticket-list row loop; a
  // social/bridge conversation with an unexpected field then just drops itself
  // instead of hiding every conversation. (dbg-20260710 bridge→inbox.)
  const rawList = Array.isArray(obj.conversations) ? obj.conversations : [];
  const conversations: SupportInboxConversation[] = [];
  for (const raw of rawList) {
    const p = SupportInboxConversationSchema.safeParse(raw);
    if (p.success) conversations.push(p.data);
  }
  const counts = SupportInboxCountsSchema.safeParse(obj.counts);
  return {
    conversations,
    counts: counts.success
      ? counts.data
      : { all: 0, open: 0, assigned_to_me: 0 },
  };
}

// ---- channels (Configurations) ------------------------------------------------

export const SupportChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['webchat', 'email', 'ai_bot', 'staff']),
  active: z.boolean(),
  updatedAt: z.union([z.string(), z.number()]).nullish(),
});
export type SupportChannel = z.infer<typeof SupportChannelSchema>;

export async function listSupportChannels(): Promise<SupportChannel[]> {
  const data = await getUnwrapped('/api/v1/tickets/channels');
  const obj = (data ?? {}) as Record<string, unknown>;
  const parsed = z
    .array(SupportChannelSchema)
    .safeParse(Array.isArray(obj.channels) ? obj.channels : []);
  return parsed.success ? parsed.data : [];
}

export async function createSupportChannel(payload: {
  name: string;
  type: SupportChannel['type'];
  active?: boolean;
}): Promise<SupportChannel> {
  const data = await sendUnwrapped('post', '/api/v1/tickets/channels', payload);
  const parsed = SupportChannelSchema.safeParse(data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Channel create response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

export async function updateSupportChannel(
  id: string,
  payload: Partial<{ name: string; type: SupportChannel['type']; active: boolean }>,
): Promise<SupportChannel> {
  const data = await sendUnwrapped(
    'patch',
    `/api/v1/tickets/channels/${encodeURIComponent(id)}`,
    payload,
  );
  const parsed = SupportChannelSchema.safeParse(data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Channel update response had an invalid format.',
    } satisfies SupportTicketApiError;
  }
  return parsed.data;
}

export async function deleteSupportChannel(id: string): Promise<void> {
  await sendUnwrapped(
    'delete',
    `/api/v1/tickets/channels/${encodeURIComponent(id)}`,
  );
}

// ---- picker options (push-vendor + New Ticket merchant) ----------------------

export interface VendorOption {
  id: string;
  name: string;
  email?: string;
}

/** GET /api/v1/admin/vendors — active vendors for the push-vendor picker. */
export async function listVendorOptions(): Promise<VendorOption[]> {
  const data = await getUnwrapped('/api/v1/admin/vendors');
  const obj = (data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(obj.items) ? obj.items : [];
  return items
    .map((v) => {
      const o = (v ?? {}) as Record<string, unknown>;
      return {
        id: String(o.id ?? ''),
        name: String(o.name ?? o.legal_name ?? 'Vendor'),
        email: typeof o.email === 'string' ? o.email : undefined,
      };
    })
    .filter((v) => v.id.length > 0);
}

export interface MerchantOption {
  id: string;
  name: string;
  email?: string;
}

/** GET /api/v1/admin/merchants?q= — merchant picker for admin-created tickets. */
export async function searchMerchantOptions(
  q: string,
): Promise<MerchantOption[]> {
  const data = await getUnwrapped(
    `/api/v1/admin/merchants${buildQs({ q: q || undefined, limit: 20 })}`,
  );
  const obj = (data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(obj.merchants) ? obj.merchants : [];
  return items
    .map((v) => {
      const o = (v ?? {}) as Record<string, unknown>;
      return {
        id: String(o.id ?? ''),
        name: String(o.name ?? 'Merchant'),
        email: typeof o.email === 'string' ? o.email : undefined,
      };
    })
    .filter((v) => v.id.length > 0);
}

// ===========================================================================
// Plan 2: dispatch (vendor/department) sub-tickets.
//   POST /api/v1/tickets/:id/dispatch
//   POST /api/v1/tickets/:id/dispatches/:did/{messages,return,cancel}
//   GET  /api/v1/tickets/dispatches?department=
// Reuses the shared sendUnwrapped/getUnwrapped helpers (this file wraps axios
// via rawAxios, NOT csrfFetch — envelope + CSRF handled by the interceptor).
// ===========================================================================

export type DispatchTargetType = 'vendor' | 'department';
export type DispatchDepartment = 'Finance' | 'IT' | 'Logistics' | 'Product';
export type DispatchStatus =
  | 'Dispatched'
  | 'InProgress'
  | 'Returned'
  | 'Cancelled';

export interface DispatchRecord {
  _id: string;
  parentTicketId: string;
  targetType: DispatchTargetType;
  vendorId: string | null;
  department: DispatchDepartment | null;
  status: DispatchStatus;
  scopeNote: string;
  resolutionNote: string | null;
  createdAt: number;
  returnedAt: number | null;
  /** Plan 5 — parent ticket summary enriched BE-side for the queue table. */
  parent?: {
    _id: string;
    ticketNumber: string | null;
    subject: string;
    status: string;
  } | null;
}

export async function createDispatch(
  ticketId: string,
  body: {
    targetType: DispatchTargetType;
    vendorId?: string;
    department?: DispatchDepartment;
    scopeNote: string;
  },
): Promise<DispatchRecord> {
  const data = await sendUnwrapped(
    'post',
    `/api/v1/tickets/${encodeURIComponent(ticketId)}/dispatch`,
    body,
  );
  return data as DispatchRecord;
}

export async function returnDispatch(
  ticketId: string,
  did: string,
  resolutionNote: string,
): Promise<DispatchRecord> {
  const data = await sendUnwrapped(
    'post',
    `/api/v1/tickets/${encodeURIComponent(ticketId)}/dispatches/${encodeURIComponent(did)}/return`,
    { resolutionNote },
  );
  return data as DispatchRecord;
}

export async function cancelDispatch(
  ticketId: string,
  did: string,
): Promise<DispatchRecord> {
  const data = await sendUnwrapped(
    'post',
    `/api/v1/tickets/${encodeURIComponent(ticketId)}/dispatches/${encodeURIComponent(did)}/cancel`,
  );
  return data as DispatchRecord;
}

export async function postDispatchMessage(
  ticketId: string,
  did: string,
  payload: { content: string; attachments?: ComposeAttachment[] },
): Promise<void> {
  await sendUnwrapped(
    'post',
    `/api/v1/tickets/${encodeURIComponent(ticketId)}/dispatches/${encodeURIComponent(did)}/messages`,
    { content: payload.content, attachments: payload.attachments ?? [] },
  );
}

/**
 * GET /api/v1/tickets/:id/dispatches — a ticket's OWN dispatches (newest-first).
 * The detail page derives the ACTIVE dispatch (Dispatched|InProgress) so the
 * DispatchThread re-renders on a fresh page load (fix/dispatch-thread-reload).
 */
export async function listTicketDispatches(
  ticketId: string,
): Promise<DispatchRecord[]> {
  const data = await getUnwrapped(
    `/api/v1/tickets/${encodeURIComponent(ticketId)}/dispatches`,
  );
  const obj = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(obj.rows)
    ? obj.rows
    : Array.isArray(data)
      ? data
      : [];
  return rows as DispatchRecord[];
}

export async function listDepartmentDispatches(
  department: DispatchDepartment,
): Promise<DispatchRecord[]> {
  const data = await getUnwrapped(
    `/api/v1/tickets/dispatches${buildQs({ department })}`,
  );
  const obj = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(obj.rows)
    ? obj.rows
    : Array.isArray(data)
      ? data
      : [];
  return rows as DispatchRecord[];
}

// ---------------------------------------------------------------------------
// Plan D — Facebook (Personal) bridge device pairing (admin).
// ---------------------------------------------------------------------------

export interface BridgeDevice {
  deviceId: string;
  platform: string;
  runtime: string;
  status: 'online' | 'offline' | 'revoked';
  lastSeenAt: string | null;
  lastError: string | null;
}

export interface BridgePairResult {
  channel: { id: string; name: string; type: string; status: string };
  device: BridgeDevice & Record<string, unknown>;
  /** Plaintext device token — returned ONCE by the pair route; not retrievable again. */
  token: string;
}

async function bridgeRequest<T>(
  method: 'get' | 'post',
  path: string,
  body?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  let res: unknown;
  try {
    const r =
      method === 'get'
        ? await rawAxios.get(path)
        : await rawAxios.post(
            path,
            body ?? {},
            idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
          );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw { ok: false, code: 'NETWORK_ERROR', userMessage: NETWORK_FALLBACK } satisfies SupportTicketApiError;
    }
  }
  const unwrap = unwrapSupportEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;
  return (unwrap.data ?? {}) as T;
}

/** Create a facebook_personal bridge channel + device, minting a one-time token. */
export async function pairBridgeDevice(body: {
  name: string;
  agentUserId: string;
  platform?: string;
  fbAccountLabel?: string;
}): Promise<BridgePairResult> {
  return bridgeRequest<BridgePairResult>('post', '/api/v1/tickets/channels/bridge/pair', body, newIdempotencyKey());
}

/** List paired bridge devices for a channel (no token echoed). */
export async function listBridgeDevices(channelId: string): Promise<BridgeDevice[]> {
  const data = await bridgeRequest<{ devices?: BridgeDevice[] }>(
    'get',
    `/api/v1/tickets/channels/${encodeURIComponent(channelId)}/bridge/devices`,
  );
  return Array.isArray(data.devices) ? data.devices : [];
}

/** Revoke a bridge device — its token stops working immediately. */
export async function revokeBridgeDevice(deviceId: string): Promise<void> {
  await bridgeRequest('post', `/api/v1/tickets/bridge/devices/${encodeURIComponent(deviceId)}/revoke`, {}, newIdempotencyKey());
}

// ---------------------------------------------------------------------------
// Test seam — internals exported for unit tests only.
// ---------------------------------------------------------------------------

export const __test__ = { rawAxios, buildQs };
